import { LogOut, Timer } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/lib/auth/AuthProvider'
import { useRealtime } from '@/lib/ws/useRealtime'
import { cn } from '@/lib/utils'

const STORAGE_KEY = 'urunver.session_end_at'
const ROLE_KEY = 'urunver.session_end_role'

function parseEndsAt(raw: unknown): number | null {
  if (typeof raw !== 'string' || !raw.trim()) return null
  const ms = Date.parse(raw)
  return Number.isFinite(ms) ? ms : null
}

function readStoredEndsAt(): { endsAtMs: number | null; roleName: string } {
  try {
    const endsAtMs = parseEndsAt(localStorage.getItem(STORAGE_KEY))
    const roleName = localStorage.getItem(ROLE_KEY) || ''
    if (endsAtMs && endsAtMs > Date.now()) return { endsAtMs, roleName }
    localStorage.removeItem(STORAGE_KEY)
    localStorage.removeItem(ROLE_KEY)
  } catch {
    /* private mode */
  }
  return { endsAtMs: null, roleName: '' }
}

function persistEndsAt(endsAtMs: number | null, roleName = '') {
  try {
    if (!endsAtMs || endsAtMs <= Date.now()) {
      localStorage.removeItem(STORAGE_KEY)
      localStorage.removeItem(ROLE_KEY)
      return
    }
    localStorage.setItem(STORAGE_KEY, new Date(endsAtMs).toISOString())
    if (roleName) localStorage.setItem(ROLE_KEY, roleName)
  } catch {
    /* private mode */
  }
}

function remainingLabel(ms: number) {
  const total = Math.max(0, Math.ceil(ms / 1000))
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

export function SessionEndingBanner() {
  const { t } = useTranslation()
  const { user, logout } = useAuth()
  const stored = readStoredEndsAt()
  const [endsAtMs, setEndsAtMs] = useState<number | null>(stored.endsAtMs)
  const [roleName, setRoleName] = useState(stored.roleName)
  const [now, setNow] = useState(() => Date.now())
  const leaving = useRef(false)
  const hadUser = useRef(false)
  const fromMe = parseEndsAt(user?.session_end_at)

  const leave = useCallback(() => {
    if (leaving.current) return
    leaving.current = true
    setEndsAtMs(null)
    setRoleName('')
    persistEndsAt(null)
    void logout()
  }, [logout])

  useEffect(() => {
    if (user) {
      hadUser.current = true
      return
    }
    if (!hadUser.current) return
    setEndsAtMs(null)
    setRoleName('')
    persistEndsAt(null)
    // Çıkış tamamlandı; aynı sekmede yeniden girişte yeni uyarılar yine gösterilebilsin.
    leaving.current = false
  }, [user])

  useEffect(() => {
    if (leaving.current || !fromMe || fromMe <= Date.now()) return
    setEndsAtMs((prev) => (prev === fromMe ? prev : fromMe))
    persistEndsAt(fromMe, roleName)
  }, [fromMe, roleName])

  useRealtime(
    (event) => event === 'auth.session_ending',
    (msg) => {
      if (leaving.current) return
      const data = (msg.data && typeof msg.data === 'object' ? msg.data : {}) as Record<string, unknown>
      const ends = parseEndsAt(data.ends_at) ?? (typeof data.grace_seconds === 'number' ? Date.now() + data.grace_seconds * 1000 : null)
      const nextRole = typeof data.role_name === 'string' ? data.role_name : ''
      if (ends && ends > Date.now()) {
        setEndsAtMs(ends)
        persistEndsAt(ends, nextRole)
      }
      if (nextRole) setRoleName(nextRole)
    },
  )

  useEffect(() => {
    if (!endsAtMs) return
    const id = window.setInterval(() => setNow(Date.now()), 250)
    return () => window.clearInterval(id)
  }, [endsAtMs])

  const left = endsAtMs ? endsAtMs - now : 0

  useEffect(() => {
    if (!endsAtMs) return
    const wait = endsAtMs - Date.now()
    if (wait <= 0) {
      leave()
      return
    }
    const id = window.setTimeout(() => leave(), wait)
    return () => window.clearTimeout(id)
  }, [endsAtMs, leave])

  const visible = !!endsAtMs && left > 0
  const urgent = left <= 10_000

  const subtitle = useMemo(() => {
    if (roleName) return t('auth.sessionEnding.roleHint', { role: roleName })
    return t('auth.sessionEnding.hint')
  }, [roleName, t])

  if (!visible) return null

  return (
    <div
      role="alert"
      className={cn(
        'fixed top-4 left-4 z-[100] w-[min(100vw-2rem,22rem)] rounded-xl border bg-popover p-3.5 shadow-lg',
        urgent ? 'border-destructive/40' : 'border-warning/40',
      )}
    >
      <div className="flex items-start gap-3">
        <span className={cn('flex size-9 shrink-0 items-center justify-center rounded-lg border', urgent ? 'border-destructive/20 bg-destructive/10 text-destructive' : 'border-warning/25 bg-warning/12 text-warning')}>
          <Timer className="size-[18px]" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">{t('auth.sessionEnding.title')}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>
          <p className={cn('mt-2 font-mono text-lg tabular-nums', urgent ? 'text-destructive' : 'text-foreground')}>{remainingLabel(left)}</p>
          <Button
            type="button"
            size="sm"
            variant={urgent ? 'destructive' : 'default'}
            className="mt-3"
            onClick={leave}
          >
            <LogOut />
            {t('auth.sessionEnding.logoutNow')}
          </Button>
        </div>
      </div>
    </div>
  )
}
